import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

/**
 * Almacena los tokens JWT.
 *
 * Nota de seguridad: se usa sessionStorage (se borra al cerrar la pestaña/navegador)
 * en lugar de localStorage, para reducir la ventana de exposicion ante un XSS.
 * La opcion mas segura en produccion es que el backend entregue el refresh token
 * en una cookie httpOnly + Secure + SameSite=Strict (inaccesible a JavaScript) y
 * que el frontend solo maneje el access token en memoria. Este servicio centraliza
 * el acceso a los tokens para que ese cambio, si se decide, quede en un solo lugar.
 */
@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private readonly accessTokenSubject = new BehaviorSubject<string | null>(this.readAccessToken());
  readonly accessToken$ = this.accessTokenSubject.asObservable();

  saveTokens(accessToken: string, refreshToken: string): void {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    this.accessTokenSubject.next(accessToken);
  }

  getAccessToken(): string | null {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  }

  clear(): void {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    this.accessTokenSubject.next(null);
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  private readAccessToken(): string | null {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }
}
